'use client';

import { useEffect } from 'react';

/**
 * Boots Datadog RUM + Qurvo on first client render. The underlying
 * `initAnalytics()` is idempotent (module-level `initialized` flag in
 * client/bootstrap/analytics.ts), so it's safe to mount alongside the
 * legacy TmaApp (apps/client/App.tsx:29 also calls it during the
 * migration window).
 *
 * The analytics module — and with it `@datadog/browser-rum`,
 * `@datadog/browser-rum-react`, and `@qurvo/sdk-browser` — is loaded
 * via dynamic `import()` scheduled on `requestIdleCallback` so it
 * never competes with the initial paint or hydration. Falls back to
 * `setTimeout(..., 0)` in browsers without idle-callback support.
 */
export function AnalyticsInitializer() {
  useEffect(() => {
    let cancelled = false;

    const run = () => {
      if (cancelled) return;
      void import('@/bootstrap/analytics')
        .then((mod) => {
          if (cancelled) return;
          mod.initAnalytics();
        })
        .catch((err) => {
          console.error('[AnalyticsInitializer] failed to load analytics:', err);
        });
    };

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(run);
      return () => {
        cancelled = true;
        w.cancelIdleCallback?.(id);
      };
    }

    const id = window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, []);

  return null;
}
